<script setup lang="ts">
interface Submit {
  voucherNumber: string
}

const { executeRecaptcha } = useGoogleRecaptcha()
const reservationStore = useReservationStore()
const voucher = ref()
const submittingNext = ref(false)
const next = async () => {
  submittingNext.value = true
  try {
    const [{ manufacturers }, contractors] = await Promise.all([
      $fetch(`/store-api/manufacturer/by-product/${voucher.value.product}`),
      $fetch('https://kalendarz.emotivo.pl/api/getcontractors')
    ])
    if (manufacturers.length === 0) {
      voucher.value.status = 'error'
      return
    }
    reservationStore.getVoucher({ voucher: toValue(voucher), manufacturers, contractors })
  } catch {
    voucher.value.status = 'error'
  } finally {
    submittingNext.value = false
  }
}

const getActive = (status: string) => {
  return status === 'ACTIVE'
}
const getRealized = (status: string) => {
  return status === 'REALIZED'
}

const getBlocked = (status: string) => {
  return status === 'BLOCKED'
}

const getOptions = (options) => {
  const list = []
  for (let i = 0; i < options.length; i++) {
    list.push(`${options[i].group}: ${options[i].option}`)
  }
  return list.join(', ')
}
const oldGr = (oldGr: boolean, status: string, type: string) => {
  if (oldGr && getActive(status) && type === 'Single use voucher') {
    setTimeout(() => {
      window.location.href = 'https://old.go-racing.pl/rezerwacja'
    }, 3000)
    return true
  }
  return false
}

const submit = async (data: Submit, node) => {
  node.clearErrors()
  const token = await executeRecaptcha('voucher')
  try {
    voucher.value = await $fetch(`/store-api/voucher/${data.voucherNumber}`, {
      method: 'POST',
      body: { token }
    })
  } catch (e: any) {
    
    node.setErrors(
      e.data.message ? e.data.message : JSON.stringify(e)
    )
  }
}
</script>

<template>
  <div v-if="voucher">
    <div v-if="oldGr(voucher.oldGr, voucher.status, voucher.type)">
      <Alert type="error" class="mb-[20px]">
        Za chwilę nastąpi przekierowanie na stronę, gdzie dokończysz zgłoszenie vouchera.
      </Alert>
      <div>
        <span>Jeśli wystąpi problem z przekierowaniem, przejdź na stronę </span>
        <NuxtLink to="https://old.go-racing.pl/rezerwacja" class="underline">https://old.go-racing.pl/rezerwacja</NuxtLink>
      </div>
    </div>
    
    <div v-else-if="voucher.type === 'Single use voucher' && getActive(voucher.status)" class="md:w-2/5">
      <div class="text-18 md:text-20 font-bold mb-[15px]">
        Twój voucher
      </div>
      <div class="font-bold">
        {{ voucher.name }}
      </div>
      <div v-if="voucher.options.length" class="font-bold">
        {{ getOptions(voucher.options) }}
      </div>
      <div>Numer: {{ voucher.hash }}</div>
      <div>Aktywny do: {{ useLocaleDate(new Date(voucher.expireDate)) }}</div>
      <button :disabled="submittingNext" class="btn btn-action mt-[15px]" @click="next()">
        <span class="text-16">Dalej</span>
        <i class="icon-arrow-button text-[10px]" />
        <LoadingIndicator v-if="submittingNext" class="text-white h-[16px] w-[16px] ml-[5px] absolute" />
      </button>
    </div>
    
    <div v-else-if="voucher.type === 'Gift Card' && getActive(voucher.status)" class="md:w-2/5">
      <div class="text-18 md:text-20 font-bold mb-[15px]">
        Karta podarunkowa
      </div>
      <div>Dostępne środki: {{ voucher.amount }} <span class="uppercase">{{ voucher.currency }}</span></div>
      <div>Numer: {{ voucher.hash }}</div>
      <div>Aktywny do: {{ voucher.expireDate }}</div>
      <div class="font-bold my-[15px]">
        Użyj tej karty jako płatność podczas zakupów.
      </div>
      <NuxtLink to="/" class="btn btn-action mt-[15px]">
        <span>Zrób zakupy</span>
        <i class="icon-arrow-button text-[10px]" />
      </NuxtLink>
    </div>


    <div v-else-if="voucher.type === 'Single use voucher' && getBlocked(voucher.status)">
      <Alert type="error" class="mb-[20px]">
        Voucher jest zablokowany.
      </Alert>
    </div>


    <div v-else>
      <Alert type="error" class="mb-[20px]">
        {{ voucher.message || 'Błąd.' }}
      </Alert>
      <NuxtLink v-if="!voucher.message" to="/kontakt" class="block underline">
        Skontaktuj się z nami
      </NuxtLink>
      <div v-else class="md:w-2/5">
        <div>Numer: {{ voucher.hash }}</div>
        <div v-if="getRealized(voucher.status)">
          <div class="font-bold my-[15px]">
            Zrób nowe zakupy!
          </div>
          <NuxtLink to="/" class="btn btn-action mt-[15px]">
            <span>Zrób zakupy</span>
            <i class="icon-arrow-button text-[10px]" />
          </NuxtLink>
        </div>
      </div>
    </div>

  </div>

  <div v-else class="flex flex-col md:flex-row justify-between">
    <div class="md:w-2/5">
      <div class="text-18 md:text-20 font-bold mb-[15px]">
        Wpisz numer z vouchera lub karty podarunkowej
      </div>
      <FormKit
        type="form"
        id="voucher-form"
        :actions="false" 
        :incomplete-message="false"
        @submit="submit"
      >
        <FormKit
          type="text"
          name="voucherNumber"
          label="Numer*"
          placeholder="Numer*"
          validation="required"
          validation-visibility="blur"
          :validation-messages="{
            required: 'Pole numer nie może być puste.'
          }"
        />
        <FormKit
          type="submit"
          input-class="btn btn-action mt-[15px]"
        >
          <span class="text-16">Sprawdź</span>
          <i class="icon-arrow-button text-[10px]" />
        </FormKit>
      </FormKit>
    </div>
    <div class="md:w-2/5">
      <div class="text-16 md:text-18 font-bold mb-[15px]">
        Jak to działa?
      </div>
      <div class="mb-[15px]">
        Wpisz numer z vouchera/karty podarunkowej, następnie kliknij "Sprawdź". W ten sposób uzyskasz szczegółowe informacje dotyczące swojego vouchera/karty podarunkowej np. takie jak: data ważności. Jeśli chcesz dokonać rezerwacji postępuj zgodnie z informacjami podanymi na stronie. Podanie tych danych nie jest równoznaczne z rezerwacją prezentu.
      </div>
    </div>
  </div>
</template>
